import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, Search, CheckSquare, Square, Loader2 } from 'lucide-react';
import { WorkflowAPI } from './api';

function stripAccounts(workflow: any) {
  return {
    ...workflow,
    nodes: (workflow.nodes || []).map((node: any) => {
      const data = { ...node.data };
      for (const key of Object.keys(data)) {
        if (data[key] && typeof data[key] === 'object' && Array.isArray((data[key] as any).selected_accounts)) {
          data[key] = { ...data[key], selected_accounts: [] };
        }
      }
      return { ...node, data };
    }),
  };
}

function downloadJSON(obj: any, filename: string) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename.replace(/\s+/g, '_')}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

interface Props {
  onClose: () => void;
  currentWorkflowId?: string | null;
}

export const WorkflowExportModal: React.FC<Props> = ({ onClose, currentWorkflowId }) => {
  const { t } = useTranslation('workflow');
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  useEffect(() => {
    WorkflowAPI.getAll()
      .then((resp: any) => {
        const list: any[] = resp?.workflows ?? (Array.isArray(resp) ? resp : []);
        setWorkflows(list);
        if (currentWorkflowId) setSelected(new Set([currentWorkflowId]));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentWorkflowId]);

  const filtered = workflows.filter(w =>
    (w.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const allSelected = filtered.length > 0 && filtered.every(w => selected.has(w._id || w.id));

  const toggleAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) {
        filtered.forEach(w => next.delete(w._id || w.id));
      } else {
        filtered.forEach(w => next.add(w._id || w.id));
      }
      return next;
    });
  };

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleExport = () => {
    const toExport = workflows.filter(w => selected.has(w._id || w.id));
    if (toExport.length === 0) return;
    const stripped = toExport.map(stripAccounts);
    if (stripped.length === 1) {
      downloadJSON(stripped[0], stripped[0].name || 'workflow');
    } else {
      downloadJSON(
        { _type: 'workflow_bundle', version: '1.0', count: stripped.length, workflows: stripped },
        'workflows_bundle'
      );
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[80vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">{t('exportWorkflows')}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('searchWorkflowsPlaceholder')}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:border-blue-300 focus:outline-none"
            />
          </div>
        </div>

        {/* Select all row */}
        {!loading && filtered.length > 0 && (
          <div className="flex items-center gap-3 px-5 py-2.5 border-b border-gray-100 bg-gray-50">
            <button
              onClick={toggleAll}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              {allSelected
                ? <CheckSquare className="w-4 h-4 text-blue-500" />
                : <Square className="w-4 h-4 text-gray-400" />}
              <span>{allSelected ? t('deselectAll') : t('selectAll')}</span>
            </button>
            <span className="ml-auto text-xs text-gray-400">
              {t('exportSelectedCount', { count: selected.size })}
            </span>
          </div>
        )}

        {/* Workflow list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-8">{t('noWorkflowsFound')}</p>
          ) : (
            filtered.map(w => {
              const id = w._id || w.id;
              const isSelected = selected.has(id);
              return (
                <label
                  key={id}
                  className={`flex items-center gap-3 px-5 py-3 cursor-pointer border-b border-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(id)}
                    className="w-4 h-4 accent-blue-500 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{w.name || 'Unnamed Workflow'}</p>
                    <p className="text-xs text-gray-400">
                      {w.status} · {w.updated_at ? new Date(w.updated_at).toLocaleDateString('vi-VN') : '—'}
                    </p>
                  </div>
                  {id === currentWorkflowId && (
                    <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                      {t('currentWorkflow')}
                    </span>
                  )}
                </label>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleExport}
            disabled={selected.size === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="w-4 h-4" />
            {t('exportCount', { count: selected.size })}
          </button>
        </div>
      </div>
    </div>
  );
};
